import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  FormError,
  InputHeader,
  Spacer,
  StyledInput,
} from "../../styles/CommonElements";
import styled from "styled-components";
import Button from "../Button";
import { DatePicker, Select, Input } from "antd";
import dayjs from "dayjs";
import { formatEmailToLowercase } from "../../utils/emailUtils";

const { Option } = Select;

const CreateProfileForm = ({
  onSubmit,
  isSubmitting,
  /** Pre-filled email when the prior step already collected it (e.g. Free Gateway signup). */
  defaultEmail,
}) => {
  const {
    handleSubmit,
    register,
    setValue,
    getFieldState,
    control,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: defaultEmail ? { email: defaultEmail } : undefined,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputHeader htmlFor="firstName">First Name</InputHeader>
      <StyledStyledInput
        {...register("firstName", {
          required: "Please enter your first name",
        })}
        id="firstName"
        placeholder="Enter your first name"
        type="text"
      />
      {errors.firstName && <FormError>{errors.firstName.message}</FormError>}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="lastName">Last Name</InputHeader>
      <StyledStyledInput
        {...register("lastName", {
          required: "Please enter your last name",
        })}
        id="lastName"
        placeholder="Enter your last name"
        type="text"
      />
      {errors.lastName && <FormError>{errors.lastName.message}</FormError>}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="email">Email</InputHeader>
      <StyledStyledInput
        {...register("email", {
          required: "Please enter your email",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Please enter a valid email address",
          },
        })}
        id="email"
        placeholder="Enter your email"
        type="email"
        onChange={(e) => {
          const formattedEmail = formatEmailToLowercase(e.target.value);
          if (formattedEmail !== e.target.value) {
            setValue("email", formattedEmail);
          }
        }}
      />
      {errors.email && <FormError>{errors.email.message}</FormError>}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="phone">Phone Number</InputHeader>
      <StyledStyledInput
        {...register("phone", {
          required: "Please enter your phone number",
        })}
        id="phone"
        placeholder="Enter your phone number"
        type="tel"
      />
      {errors.phone && <FormError>{errors.phone.message}</FormError>}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="propertyType">Primary Property Type</InputHeader>
      <Controller
        name="propertyType"
        control={control}
        rules={{ required: "Please select your primary property type" }}
        render={({ field }) => (
          <StyledSelect
            {...field}
            placeholder="Select property type"
            options={[
              { value: "apartment", label: "Apartment" },
              { value: "house", label: "House" },
              { value: "condo", label: "Condo" },
              { value: "townhouse", label: "Townhouse" },
              { value: "duplex", label: "Duplex" },
              { value: "multi-family", label: "Multi-Family" },
              { value: "commercial", label: "Commercial" },
              { value: "other", label: "Other" },
            ]}
          />
        )}
      />
      {errors.propertyType && (
        <FormError>{errors.propertyType.message}</FormError>
      )}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="experience">
        Years of Landlord Experience
      </InputHeader>
      <Controller
        name="experience"
        control={control}
        rules={{ required: "Please select your experience level" }}
        render={({ field }) => (
          <StyledSelect
            {...field}
            placeholder="Select experience level"
            options={[
              { value: "0-1", label: "0-1 years" },
              { value: "1-3", label: "1-3 years" },
              { value: "3-5", label: "3-5 years" },
              { value: "5-10", label: "5-10 years" },
              { value: "10+", label: "10+ years" },
            ]}
          />
        )}
      />
      {errors.experience && <FormError>{errors.experience.message}</FormError>}

      <Spacer $marginTop="1.5rem" />

      <InputHeader htmlFor="properties">Number of Properties</InputHeader>
      <Controller
        name="properties"
        control={control}
        rules={{ required: "Please select number of properties" }}
        render={({ field }) => (
          <StyledSelect
            {...field}
            placeholder="Select number of properties"
            options={[
              { value: "1", label: "1 property" },
              { value: "2-5", label: "2-5 properties" },
              { value: "6-10", label: "6-10 properties" },
              { value: "11-20", label: "11-20 properties" },
              { value: "20+", label: "20+ properties" },
            ]}
          />
        )}
      />
      {errors.properties && <FormError>{errors.properties.message}</FormError>}

      <Spacer $marginTop="3.5rem" />

      <Button
        type="submit"
        size="large"
        isLoading={isSubmitting}
        disabled={!isValid || isSubmitting}
      >
        Create Profile
      </Button>
    </form>
  );
};

const StyledStyledInput = styled(StyledInput)`
  background: white !important;
  border: 1px solid var(--color-gray-200) !important;
  border-radius: 8px !important;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 48px;

  .ant-select-selector {
    height: 48px !important;
    background: white !important;
    padding: 0 16px !important;
    border-radius: 8px !important;
  }

  .ant-select-selection-item {
    line-height: 48px !important;
    color: black !important;
  }

  .ant-select-selection-placeholder {
    line-height: 48px !important;
    color: var(--color-gray-400) !important;
  }

  .ant-select-arrow {
    color: var(--color-gray-600) !important;
  }
`;

export default CreateProfileForm;
